import { useState } from 'react';

export const useLayerManagement = (onToolChange, redrawCanvas) => {
    const [layers, setLayers] = useState([]);
    const [selectedLayer, setSelectedLayer] = useState(null);
    const [editingLayer, setEditingLayer] = useState(null);
    const [isLayersPanelCollapsed, setIsLayersPanelCollapsed] = useState(false);

    const addLayer = (layer) => {
        setLayers(prev => [...prev, {
            id: Date.now(),
            type: layer.type,
            data: layer.data,
            name: layer.name,
            visible: true
        }]);
    };

    const addImageLayer = (img) => {
        setLayers(prev => {
            // Remove all image layers
            const nonImageLayers = prev.filter(layer => layer.type !== 'image');
            
            // Add new image layer at the bottom
            return [{
                id: Date.now(),
                type: 'image',
                data: img,
                name: 'Image',
                visible: true
            }, ...nonImageLayers];
        });
    };

    const addShapeLayer = (shape) => {
        console.log('Adding shape layer:', shape);
        
        // Create layer consistently for all shapes including textbox
        const newLayer = {
            id: Date.now(),
            type: 'shape',
            name: shape.type === 'textbox' ? 'Text' : `${shape.type} ${layers.filter(l => l.type === 'shape').length + 1}`,
            visible: true,
            data: shape  // Store all shape data in data property
        };

        console.log('Created new layer:', newLayer);
        setLayers(prev => [...prev, newLayer]);
        setSelectedLayer(newLayer.id);
        setEditingLayer(newLayer.id);
        return newLayer.id;
    };

    const toggleLayerVisibility = (layerId) => {
        setLayers(prev => prev.map(layer => 
            layer.id === layerId 
                ? {...layer, visible: !layer.visible}
                : layer
        ));
    };

    const moveLayer = (index, direction) => {
        setLayers(prev => {
            const newLayers = [...prev];
            if (direction === 'up' && index > 0) {
                [newLayers[index], newLayers[index - 1]] = [newLayers[index - 1], newLayers[index]];
            } else if (direction === 'down' && index < newLayers.length - 1) {
                [newLayers[index], newLayers[index + 1]] = [newLayers[index + 1], newLayers[index]];
            }
            return newLayers;
        });
    };

    const deleteLayer = (layerId) => {
        setLayers(prev => prev.filter(layer => layer.id !== layerId));
        if (selectedLayer === layerId) setSelectedLayer(null);
        if (editingLayer === layerId) setEditingLayer(null);
    };

    const updateLayerColor = (layerId, newColor) => {
        console.log('Updating color:', layerId, newColor);
        setLayers(prev => {
            return prev.map(layer => {
                if (layer.id === layerId) {
                    console.log('Found layer:', layer);
                    if (layer.type === 'shape') {
                        return {
                            ...layer,
                            data: {
                                ...layer.data,
                                color: newColor
                            }
                        };
                    } else if (layer.type === 'textbox') {
                        return {
                            ...layer,
                            color: newColor
                        };
                    }
                }
                return layer;
            });
        });
    };

    const updateLayerData = (layerId, updateFn) => {
        setLayers(prev => prev.map(layer => {
            if (layer.id !== layerId) return layer;

            // Handle textbox layers differently since their data is stored directly
            if (layer.type === 'textbox') {
                return {
                    ...layer,
                    ...updateFn  // For textbox, updateFn is the new data object
                };
            }

            // For other layers, updateFn is a function that transforms the data
            return {
                ...layer,
                data: typeof updateFn === 'function' ? updateFn(layer.data) : updateFn
            };
        }));
    };

    const handleLayerClick = (layerId) => {
        const layer = layers.find(l => l.id === layerId);
        console.log('Layer clicked:', layerId, layer);
        if (layer && (layer.type === 'shape' || layer.type === 'textbox')) {
            console.log('Setting editing layer:', layerId);
            setSelectedLayer(layerId);
            setEditingLayer(layerId);
            onToolChange('pointer');
            if (redrawCanvas) redrawCanvas();
        }
    };

    const renameLayer = (layerId, newName) => {
        setLayers(prevLayers => prevLayers.map(layer => 
            layer.id === layerId 
                ? { ...layer, name: newName }
                : layer
        ));
    };

    return {
        layers,
        selectedLayer,
        editingLayer,
        isLayersPanelCollapsed,
        setLayers,
        setSelectedLayer,
        setEditingLayer,
        setIsLayersPanelCollapsed,
        addLayer,
        addImageLayer,
        addShapeLayer,
        toggleLayerVisibility,
        moveLayer,
        deleteLayer,
        updateLayerColor,
        updateLayerData,
        handleLayerClick,
        renameLayer
    };
}; 